import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import * as api from "../api";

const TYPE_ICON = {
  abonnement_active:     "🎉",
  abonnement_rejete:     "❌",
  abonnement_en_attente: "⏳",
  abonnement_recu:       "📬",
  paiement_confirme:     "✅",
  paiement_refuse:       "❌",
  paiement_recu:         "💰",
  nouveau_message:       "💬",
  reponse_rfq:           "📋",
  nouvelle_rfq:          "📝",
  annonce_acceptee:      "✅",
  annonce_refusee:       "❌",
  annonce_en_attente:    "⏳",
};

export default function NotificationsBell() {
  const { user } = useAuth();  // ← récupérer l'utilisateur connecté
  const [notifs, setNotifs]   = useState([]);
  const [unread, setUnread]   = useState(0);
  const [open, setOpen]       = useState(false);
  const ref                   = useRef();
  const intervalRef           = useRef();

  const fetchNotifs = () => {
    // Ne rien faire si pas connecté
    if (!user) return;
    api.getNotifications()
      .then(res => {
        setNotifs(res.data.notifications || []);
        setUnread(res.data.unread || 0);
      })
      .catch(() => {});
  };

  // Réinitialiser et recharger quand l'utilisateur change (connexion / déconnexion)
  useEffect(() => {
    // Nettoyer l'état quand l'utilisateur change
    setNotifs([]);
    setUnread(0);
    setOpen(false);

    if (intervalRef.current) clearInterval(intervalRef.current);

    if (user) {
      fetchNotifs();
      intervalRef.current = setInterval(fetchNotifs, 30000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user?.id]); // ← dépend de l'ID utilisateur, pas juste de "user"

  // Fermer si clic extérieur
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Ne rien afficher si pas connecté
  if (!user) return null;

  const handleOpen = () => {
    setOpen(!open);
    if (!open && unread > 0) {
      api.marquerToutLu()
        .then(() => { setUnread(0); fetchNotifs(); })
        .catch(() => {});
    }
  };

  const handleClick = (notif) => {
    if (!notif.lu) {
      api.marquerLu(notif.id).catch(() => {});
    }
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={handleOpen}
        style={{
          position: "relative",
          width: 40, height: 40,
          borderRadius: 10,
          backgroundColor: open ? "#f0fdfa" : "white",
          border: `1px solid ${open ? "#0d9488" : "#e5e7eb"}`,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}
        title="Notifications"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={unread > 0 ? "#0d9488" : "#6b7280"} strokeWidth="2">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span style={{ position: "absolute", top: -5, right: -5, backgroundColor: "#dc2626", color: "white", borderRadius: "50%", width: 18, height: 18, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid white" }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 10px)", right: 0,
          width: 340, maxHeight: 420,
          backgroundColor: "white", border: "1px solid #e5e7eb",
          borderRadius: 14, boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          zIndex: 1000, overflow: "hidden",
          display: "flex", flexDirection: "column"
        }}>
          {/* Header */}
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#f9fafb" }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#1f2937" }}>🔔 Notifications</span>
            {unread > 0 && (
              <span style={{ fontSize: 11, backgroundColor: "#dc2626", color: "white", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>
                {unread} non lue{unread > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Liste */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {notifs.length === 0 ? (
              <div style={{ padding: "40px 20px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
                Aucune notification
              </div>
            ) : notifs.map(n => (
              <Link
                key={n.id}
                to={n.lien || "/mon-espace"}
                onClick={() => handleClick(n)}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 12,
                  padding: "13px 16px",
                  backgroundColor: n.lu ? "white" : "#f0fdfa",
                  borderBottom: "1px solid #f9fafb",
                  textDecoration: "none",
                  transition: "background 0.15s"
                }}
              >
                <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>
                  {TYPE_ICON[n.type] || "🔔"}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: n.lu ? 500 : 700, color: "#1f2937", lineHeight: 1.3, marginBottom: 3 }}>
                    {n.titre}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>
                    {n.message}
                  </div>
                  <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>
                    {n.created_at ? new Date(n.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                  </div>
                </div>
                {!n.lu && (
                  <div style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#0d9488", flexShrink: 0, marginTop: 5 }} />
                )}
              </Link>
            ))}
          </div>

          {/* Footer */}
          {notifs.length > 0 && (
            <div style={{ padding: "10px 16px", borderTop: "1px solid #f3f4f6", textAlign: "center", backgroundColor: "#f9fafb" }}>
              <button
                onClick={() => {
                  api.marquerToutLu().then(() => { setUnread(0); fetchNotifs(); });
                }}
                style={{ fontSize: 12, color: "#0d9488", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
              >
                Tout marquer comme lu
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}